<template>
	<Default :loading="loading">
		<GeolocationWarnings v-if="geolocationError" :error="geolocationError" :granted="granted"
			:support="supported" />
		<div v-else class="w-full h-full relative md:flex">
			<div
				class="grow flex flex-col md:translate-x-0 transform transition-transform duration-300 ease-in-out translate-x-0 h-full overflow-x-auto">
				<!-- Container -->
				<div class="grow p-3 bg-gray-100">
					<!-- Page header -->
					<div class="md:flex md:items-center">
						<!-- Left: Title -->
						<h1 class="text-2xl md:text-3xl text-gray-800 font-bold mb-2 md:mb-0 flex-1">Leads alquilados
						</h1>
						<!-- More actions -->
						<div class="flex flex-col md:flex-row gap-y-2 md:gap-x-2">
							<Button :disabled="!user.permissions.includes('deal.rented.markAsPaid')"
								v-if="selectedLeads.length > 0" @click="markAsPaid">
								<icon name="ri-check-line" class="mr-1" />
								Marcar como pagado ({{ selectedLeads.length }})
							</Button>
							<Button @click="download"
								:disabled="!user.permissions.includes('deal.rented.download') || !items.length"
								:loading="downloading">
								<icon name="ri-download-line" class="mr-1" />
								Descargar registro
							</Button>
						</div>
					</div>

					<Accordion title="Filtros" class="bg-white mt-3">
						<div class="grid md:grid-cols-2 xl:grid-cols-4 gap-2 my-2">
							<Multiselect v-model="selectedProjects" :custom-label="p => p.label" trackBy="name"
								placeholder="Seleccionar proyectos" :hideSelected="true" :multiple="true"
								:options="projects" :showNoResults="true" :closeOnSelect="false" :preserveSearch="true"
								:clearOnSelect="false" selectLabel="enter para seleccionar" selectedLabel="seleccionado"
								deselectLabel="enter para eliminar">
								<template #noResult>Sin resultados. Realiza otra búsqueda. </template>
								<template #noOptions>No se han encontrado proyectos. </template>
							</Multiselect>

							<div>
								<input class="w-full form-input" type="datetime-local" name="from"
									v-model="options['from']" @input="onchangeDateFilters" />
								<small class="w-full p-1">Desde</small>
							</div>
							<div>
								<input class="w-full form-input" type="datetime-local" name="to" v-model="options['to']"
									@input="onchangeDateFilters" />
								<small class="w-full p-1">Hasta</small>
							</div>

							<div>
								<select v-model="options.paid" class="form-select flex-1 md:flex-auto w-full">
									<option :value="undefined">Todos los leads</option>
									<option value="true">Pagado</option>
									<option value="false">No pagado</option>
								</select>
								<small class="w-full p-1">Estado de pago</small>
							</div>
						</div>
					</Accordion>

					<!-- Table -->
					<div class="bg-white rounded-sm border border-gray-200 relative mt-3">
						<div class="flex flex-wrap gap-6 items-center justify-between py-2 px-4">
							<header class="flex-1 font-semibold text-slate-800 whitespace-nowrap">
								<div>
									<p>
										Resultados:
										<span class="text-slate-400">{{ total }}</span>
									</p>
								</div>
							</header>

							<div class="gap-2 flex items-center">
								<div>
									Mostrar:
									<select v-model="options.limit" class="ml-2 form-select flex-1 md:flex-auto"
										@change="onChangeLimit">
										<option :value="5">5</option>
										<option :value="10">10</option>
										<option :value="20">20</option>
										<option :value="50">50</option>
										<option :value="100">100</option>
										<option :value="150">150</option>
									</select>
								</div>
							</div>
						</div>

						<!-- Table -->
						<div class="overflow-x-auto" v-if="!loading && items.length">
							<table class="table-auto w-full">
								<!-- Table header -->
								<thead
									class="text-xs font-semibold uppercase text-gray-500 bg-gray-50 border-b border-t border-gray-200">
									<tr>
										<th v-if="leadsNotPaid.length > 0"
											class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap text-left">
											<input type="checkbox" class="form-checkbox"
												@change="toggleAllLeadsSelection" />
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center">
													<p class="uppercase mr-1">Usuario</p>
												</button>
											</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center">
													<p class="uppercase mr-1">Email</p>
												</button>
											</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center">
													<p class="uppercase mr-1">Precio</p>
												</button>
											</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center">
													<p class="uppercase mr-1">Pagado</p>
												</button>
											</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center">
													<p class="uppercase mr-1">Lead</p>
												</button>
											</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center">
													<p class="uppercase mr-1">Fecha</p>
												</button>
											</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center">
													<p class="uppercase mr-1">Proyecto</p>
												</button>
											</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center">
													<p class="uppercase mr-1">Origen</p>
												</button>
											</div>
										</th>
									</tr>
								</thead>
								<!-- Table body -->
								<tbody class="text-sm divide-y divide-gray-200">
									<tr class="even:bg-gray-100 hover:bg-gray-200" v-for="item in items"
										:key="item._id">
										<td v-if="leadsNotPaid.length > 0"
											class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap place-content-start">
											<input v-if="!item.paid" type="checkbox" class="form-checkbox"
												:checked="isLeadSelected(item._id)"
												@change="toggleLeadSelection(item, ($event.target as HTMLInputElement).checked)" />
										</td>
										<td
											class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap place-content-start">
											{{
												item.userFullname }}</td>
										<td
											class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap place-content-start">
											{{ item.userEmail
											}}</td>
										<td
											class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap place-content-start">
											{{ item.price }}€
										</td>
										<td
											class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap place-content-start">
											{{ item.paid ? 'Sí' : 'No' }}
										</td>
										<td
											class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap place-content-start">
											{{ item.lead }}
										</td>
										<td
											class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap place-content-start">
											{{
												dayjs(item.createdAt).format('DD/MM/YY - HH:mm') }}</td>
										<td
											class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap place-content-start">
											{{
												item.projectLabel }}</td>
										<td
											class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap place-content-start">
											{{ item.originLabel
											}}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>

					<!-- Pagination -->
					<div class="mt-8 mb-24" v-if="!loading && items.length">
						<Pagination v-show="total > options.limit" :results="items.length" :skip="options.skip"
							:limit="options.limit" :total="total" @page="updatePage" />
					</div>

					<!-- No results -->
					<div class="bg-white p-4 rounded-sm border border-gray-200 mt-3"
						v-if="(!loading && !items.length) || !!error.message">
						<Banner type="error"
							:open="!!error?.message || (Array.isArray(error?.errors) && error.errors.length > 0)" fixed>
							<ul v-if="Array.isArray(error?.errors) && error.errors.length > 0">
								<li v-for="(err, i) in error?.errors" :key="i">
									<p class="p-3">- {{ err.message }}</p>
								</li>
							</ul>
							<p v-else-if="error.message">{{ error.message }}</p>
						</Banner>

						<div class="w-full flex justify-center p-4" v-if="!loading && !items.length">
							<div class="text-center py-6">
								<div
									class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-t from-red-300 to-red-100 mb-4">
									<icon name="ri-information-line" scale="2" />
								</div>
								<h2 class="text-2xl text-gray-800 font-bold mb-2">No se han encontrado registros.</h2>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</Default>
</template>

<script setup lang="ts">
import { ValidationError } from '@/errors/ValidationError'
import { user } from '@/user'
import dayjs from 'dayjs'
import { Ref, computed, onMounted, reactive, ref, watch } from 'vue'
import Multiselect from 'vue-multiselect'
import { useRoute, useRouter } from 'vue-router'
import Accordion from '../components/Accordion.vue'
import Banner from '../components/Banner.vue'
import Button from '../components/Button.vue'
import GeolocationWarnings from '../components/geolocation/GeolocationWarnings.vue'
import Pagination from '../components/Pagination.vue'
import { Project } from '../interfaces/Project'
import { get, put } from '../services/api'
import { useGeolocation } from '../services/geolocation'
import Default from './Default.vue'

const router = useRouter()
const route = useRoute()

const { coordinates, supported, granted, error: geolocationError } = useGeolocation()

interface DealRented {
	_id: string
	paid: boolean
	email: string
	price: number
	createdAt: Date
	lead: string
	user: string
	project: string
	originLabel: string
	userFullname: string
	userEmail: string
	projectLabel: string
}

const loading = ref(false)
const downloading = ref(false)
const items = ref<DealRented[]>([])
const total = ref(0)
const error = ref<ValidationError>(<any>{})
const projects = ref<Project[]>([])
const selectedProjects = ref<Project[]>([])
const selectedLeads = ref<DealRented[]>([])

const leadsNotPaid = computed(() => items.value.filter(item => !item.paid))

const options: Record<string, any> = reactive({
	...route.query,
	limit: 20,
	skip: 0,
	...(route.query['from'] && typeof route.query['from'] === 'string' && { ['from']: dayjs(route.query['from']).format('YYYY-MM-DDTHH:mm') }),
	...(route.query['to'] && typeof route.query['to'] === 'string' && { ['to']: dayjs(route.query['to']).format('YYYY-MM-DDTHH:mm') }),
	...(route.query.paid && typeof route.query.paid === 'string' && { paid: route.query.paid === 'true' })
})

const search = async () => {
	if (Object.keys(coordinates.value).length === 0) return
	loading.value = true
	error.value = <any>{}
	try {
		items.value = []

		const sendOptions = { ...options }

		if (options['to']) {
			sendOptions['to'] = new Date(options['to']).toISOString()
		} else {
			delete sendOptions['to']
		}
		if (options['from']) {
			sendOptions['from'] = new Date(options['from']).toISOString()
		} else {
			delete sendOptions['from']
		}

		await router.replace({
			query: sendOptions
		})

		const { data, pagination } = await get({ name: 'deal.rented', query: sendOptions }, coordinates.value)
		total.value = pagination.total
		items.value = data
	} catch (err) {
		error.value = err
	}
	loading.value = false
}

const download = async () => {
	if (!items.value.length) return
	downloading.value = true
	try {
		const { limit, skip, ...sendOptions } = options

		if (options['from']) {
			sendOptions['from'] = new Date(options['from']).toISOString()
		} else {
			delete sendOptions['from']
		}
		if (options['to']) {
			sendOptions['to'] = new Date(options['to']).toISOString()
		} else {
			delete sendOptions['to']
		}
		const { data } = await get({ name: 'deal.rented.download', query: sendOptions }, coordinates.value)
		const base64Uri = `data:@file/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${data}`
		const blob = await fetch(base64Uri).then(res => res.blob())
		const link = document.createElement('a')
		link.href = window.URL.createObjectURL(blob)
		link.download = `DEALS_RENTED_${dayjs().format('DD-MM-YYYY')}.xlsx`
		link.click()
		link.remove()
	} catch (err) {
		error.value = err
	}
	downloading.value = false
}

const fetchProjects = async () => {
	loading.value = true
	try {
		const { data } = await get({ name: 'project' })
		projects.value = data.filter((project: Project) => user.value?.projects?.includes(project.name)) || []
		selectedProjects.value = [...((<string>route.query.projects)?.split(',')?.length ? projects.value.filter(p => route.query.projects?.includes(p.name)) : [])]
	} catch (err) {
		error.value = err
	}
	loading.value = false
}

watch(selectedProjects, async () => {
	options.skip = 0
	if (selectedProjects.value.length) {
		options.projects = selectedProjects.value.map(p => p.name).join(',')
	} else {
		delete options.projects
	}
})

watch(
	options,
	() => {
		search()
	},
	{ deep: true }
)

onMounted(async () => {
	await fetchProjects()
	await search()
})

const onchangeDateFilters = () => {
	options.skip = 0
}

const onChangeLimit = () => {
	options.skip = 0
}

const updatePage = (page: Ref) => {
	options.skip = page.value == -1 ? 0 : (page.value - 1) * options.limit
}

const isLeadSelected = (id: string) => selectedLeads.value.some(l => l._id === id)

const toggleLeadSelection = (item: DealRented, checked: boolean) => {
	if (item.paid) return
	if (checked) {
		if (!selectedLeads.value.some(l => l._id === item._id)) {
			selectedLeads.value = [...selectedLeads.value, item]
		}
	} else {
		selectedLeads.value = selectedLeads.value.filter(l => l._id !== item._id)
	}
}

const toggleAllLeadsSelection = (event: Event) => {
	const checked = (event.target as HTMLInputElement).checked
	items.value.forEach(item => {
		if (!item.paid) {
			toggleLeadSelection(item, checked)
		}
	})
}

const markAsPaid = async () => {
	loading.value = true
	try {
		if (!user.value?.permissions.includes('deal.rented.markAsPaid')) return
		const payload = {
			dealsData: selectedLeads.value.map(l => ({ _id: l._id, project: l.project }))
		}

		await put({ name: 'deal.rented.markAsPaid' }, payload, undefined, coordinates.value)
		selectedLeads.value = []
		await search()
	} catch (error) {
		error.value = error
	} finally {
		loading.value = false
	}
}
</script>
